/**
 * Product path utilities
 */

import type { Product } from './product-types';

/**
 * Convert a string to URL-friendly format (lowercase, hyphens instead of spaces)
 */
export function dasherize(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Generate the URL path for a product
 *
 * A blank, whitespace or punctuation-only title dasherizes to '' — fall back to
 * 'untitled', as the listing grids and getStaticPaths do. An empty segment left
 * `/product/<id>/`, which lands on the slugless [id] route: a props-less shell
 * that renders a skeleton and fetches on the client instead of the SSG page.
 */
export function getProductPath<T extends Pick<Product, 'id' | 'title'>>(product: T | null | undefined): string {
  if (!product?.id) return '/';
  return `/product/${product.id}/${dasherize(product.title) || 'untitled'}`;
}

/**
 * Check if a path matches a product path pattern
 */
export function isProductPath(path: string): boolean {
  return /^\/product\/\d+/.test(path);
}

/**
 * Extract product ID from a product path
 */
export function extractProductIdFromPath(path: string): string | null {
  const match = path.match(/^\/product\/(\d+)/);
  return match ? match[1] : null;
}
